// cleanup_convert_to_typescript_file_SECURITY_CONTACT

import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Input } from '@glu/form-components';
import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';

import { sendActivationCode, validateActivationCode } from 'api/user/service';
import { SecurityContact } from 'proptypes/user';
import useLocale from 'components/useLocale';

import useStyles from './SecurityActivation.styles';
import reducer from './SecurityActivation.reducer';
import { REDUCER_ACTIONS } from './securityContacts.constants';

const ACTIVATION_CODE = 'activationCode';

const { HANDLE_ERROR, HANDLE_INPUT_CHANGE, HANDLE_RESPONSE, SET_SENDING, VERIFY_CODE } =
  REDUCER_ACTIONS;

const initialState = {
  activationCode: '',
  errorMessage: '',
  sending: false
};

export const SecurityActivationComponent = ({
  contact,
  contact: { contactType, errorMessage: errorMessageFromProps, value },
  disabled,
  name,
  onReset,
  onVerified
}) => {
  const { groupInput, inputsGroup } = useStyles();
  const [{ activationCode, errorMessage, sending }, dispatch] = useReducer(reducer, initialState);
  const locale = useLocale();
  const id = [name, ACTIVATION_CODE].filter(Boolean).join('-');

  const handleInputChange = (event = {}) => {
    const { target = {} } = event;
    const { value = '' } = target;
    const activationCode = value.replace(/\s/g, '');
    dispatch({ type: HANDLE_INPUT_CHANGE, payload: { activationCode, errorMessage: '' } });
  };

  const handleError = ({ data: [{ code, userMessage } = {}] = [{}] } = {}) => {
    dispatch({
      type: HANDLE_ERROR,
      payload: {
        errorMessage: locale[code] || userMessage || locale['common.server.error'],
        sending: false
      }
    });
  };

  const handleResponse = (data) => {
    dispatch({ type: HANDLE_RESPONSE, payload: { errorMessage: '', sending: false } });
    if (!data.success) {
      handleError(data);
    }
  };

  const sendCode = () => {
    dispatch({ type: SET_SENDING, payload: true });

    return sendActivationCode({
      contactType,
      default: false,
      value
    })
      .then(handleResponse)
      .catch(handleError);
  };

  const verifyCode = () => {
    dispatch({ type: SET_SENDING, payload: true });
    if (activationCode === '') {
      dispatch({
        type: VERIFY_CODE,
        payload: {
          errorMessage: locale['validation.required'],
          sending: false
        }
      });
    } else {
      validateActivationCode(activationCode)
        .then((data) => {
          handleResponse(data);
          const { defaultInvalid, ...rest } = contact;

          return onVerified({
            ...rest,
            errorMessage: data.success ? '' : errorMessageFromProps,
            verified: data.success,
            default: !!defaultInvalid
          });
        })
        .catch(handleError);
    }
  };

  return (
    <div className={classnames({ 'has-error': errorMessage })}>
      <label htmlFor={id}>{locale['profile.enterCode']}</label>
      <div className={inputsGroup}>
        <Input
          className={groupInput}
          disabled={disabled}
          htmlId={id}
          name={id}
          value={activationCode}
          onChange={handleInputChange}
          data-qa="verify-code-input"
        />
        <Button
          className="btn btn-primary"
          dataQa="verify-code-button"
          disabled={disabled || sending}
          onClick={verifyCode}
          text={locale['common.activate']}
          type="button"
          variant={PRIMARY}
        />
        <Button
          className="btn btn-link"
          disabled={disabled || sending}
          onClick={sendCode}
          text={locale['common.resend']}
          type="button"
          variant={TERTIARY}
        />
        <Button
          className="btn btn-link"
          disabled={disabled}
          onClick={onReset}
          text={locale['common.cancel']}
          type="button"
          variant={TERTIARY}
        />
      </div>
      {!errorMessage && !sending && <small>{locale['profile.codeSent']}</small>}
      {errorMessage && (
        <small className="help-block" data-qa="verify-code-help-text">
          {errorMessage}
        </small>
      )}
    </div>
  );
};

SecurityActivationComponent.propTypes = {
  contact: SecurityContact,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  onReset: PropTypes.func,
  onVerified: PropTypes.func
};

SecurityActivationComponent.defaultProps = {
  contact: {},
  disabled: false,
  name: '',
  onReset() {},
  onVerified() {}
};

export default SecurityActivationComponent;
